import { Workflow, Step } from './Workflow';
import { useMemo, useCallback } from 'react';
import { keys, reject, not, findIndex, equals, prop } from 'ramda';

const useWorkflowHelpers = (workflow: Workflow) => {
  const steps = useMemo(() => {
    const { stepsEnabled } = workflow;
    return (keys(reject(not, stepsEnabled)) as unknown) as Step[];
  }, [workflow]);

  const paths = useMemo(() => workflow.paths, [workflow]);

  const stepNumber = useCallback(
    (step: Step) => {
      const index = findIndex(equals(step), steps);
      if (index === -1) {
        throw new Error(`Step ${step} is not in workflow`);
      }
      return index;
    },
    [steps],
  );

  const hasStep = useCallback(
    (step: Step) => {
      return findIndex(equals(step), steps) !== -1;
    },
    [steps],
  );

  const pathFor = useCallback(
    (step: Step) => {
      const path = prop(step, paths);

      if (path === undefined) {
        throw new Error(`No path defined for ${step}`);
      }
      return path;
    },
    [paths],
  );

  const stepAfter = useCallback(
    (step: Step) => {
      const index = stepNumber(step);
      const nextIndex = index + 1;
      if (nextIndex >= steps.length) {
        throw new Error(`There are no step after ${step}`);
      }
      return steps[nextIndex];
    },
    [steps, stepNumber],
  );

  const pathAfter = useCallback(
    (step: Step) => {
      const nextStep = stepAfter(step);

      if (nextStep === null) {
        throw new Error(`No path after ${step}`);
      }

      return pathFor(nextStep);
    },
    [pathFor, stepAfter],
  );

  const restartPath = useMemo(() => pathFor(workflow.restartStep), [
    workflow.restartStep,
    pathFor,
  ]);

  const timeoutRestartPath = useMemo(() => workflow.timeoutRestartPath, [
    workflow.timeoutRestartPath,
  ]);

  return {
    steps,
    stepNumber,
    pathFor,
    stepAfter,
    pathAfter,
    hasStep,
    restartPath,
    timeoutRestartPath,
  };
};

export default useWorkflowHelpers;
